import styled from 'styled-components'
import { Layout, Flex as AntFlex, Button as AntButton } from 'antd'
import { SPACING, MEDIA, TYPOGRAPHY, FONT_WEIGHT } from '../../../styles/theme'

/**
 * Styled components for AppHeader
 * Replaces inline styles with reusable, semantic components
 */

const { Header } = Layout

interface StyledHeaderProps {
  $background?: string
  $borderColor?: string
}

// Main header with fixed positioning and border
export const StyledHeader = styled(Header)<StyledHeaderProps>`
  &.ant-layout-header {
    background: ${(props) => props.$background};
    border-bottom: 1px solid ${(props) => props.$borderColor};
    padding: 0 ${SPACING.xl};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    width: 100%;

    ${MEDIA.mobile} {
      padding: 0 ${SPACING.md};
    }
  }
`

// Container for header content
export const HeaderContent = styled(AntFlex)`
  &.ant-flex {
    height: 100%;
    gap: 16px;

    ${MEDIA.mobile} {
      gap: 8px;
    }
  }
`

// Left side container - shrinks on mobile
export const LeftSection = styled(AntFlex)`
  &.ant-flex {
    gap: 24px;
    flex-shrink: 0;

    ${MEDIA.mobile} {
      gap: 8px;
      min-width: 0;
      flex: 0 1 auto;
    }
  }
`

// Right side container - doesn't shrink
export const RightSection = styled(AntFlex)`
  &.ant-flex {
    gap: 8px;
    flex-shrink: 0;
  }
`

// Settings button with centered icon
export const SettingsButton = styled(AntButton)`
  &.ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

// Upgrade to Pro button with gradient styling
export const UpgradeButton = styled(AntButton)`
  &.ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    font-weight: ${FONT_WEIGHT.semibold};
    font-size: ${TYPOGRAPHY.caption.fontSize};
    padding: 4px 12px;
    height: 32px;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);

    &:hover {
      background: linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%) !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }

    .anticon {
      font-size: ${TYPOGRAPHY.caption.fontSize};
    }

    ${MEDIA.mobile} {
      padding: 4px 8px;
      font-size: ${TYPOGRAPHY.caption.fontSize};

      span:not(.anticon) {
        display: none;
      }
    }
  }
`

interface DashboardButtonProps {
  $variant: 'admin' | 'partner'
}

// Dashboard button for admin/partner portals
export const DashboardButton = styled(AntButton)<DashboardButtonProps>`
  &.ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-weight: ${FONT_WEIGHT.medium};
    font-size: ${TYPOGRAPHY.caption.fontSize};
    padding: 4px 12px;
    height: 32px;
    border-radius: 6px;
    transition: all 0.2s ease;

    ${(props) =>
      props.$variant === 'admin'
        ? `
      background: linear-gradient(135deg, #f5a623 0%, #e08e0b 100%);
      border: none;
      color: white;
      box-shadow: 0 2px 6px rgba(245, 166, 35, 0.3);

      &:hover {
        background: linear-gradient(135deg, #e09515 0%, #c77d08 100%) !important;
        color: white !important;
        box-shadow: 0 3px 10px rgba(245, 166, 35, 0.4);
      }
    `
        : `
      background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
      border: none;
      color: white;
      box-shadow: 0 2px 6px rgba(82, 196, 26, 0.3);

      &:hover {
        background: linear-gradient(135deg, #45a815 0%, #2d8a09 100%) !important;
        color: white !important;
        box-shadow: 0 3px 10px rgba(82, 196, 26, 0.4);
      }
    `}

    .anticon {
      font-size: ${TYPOGRAPHY.caption.fontSize};
    }

    ${MEDIA.mobile} {
      padding: 4px 8px;
      min-width: 32px;

      span:not(.anticon) {
        display: none;
      }
    }
  }
`
